import React from 'react';
import { ArrowLeft, Lock, Mail, Smartphone, AlertTriangle, Trash2, FolderArchive, Clock, HelpCircle, CheckCircle2, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function KontoLoeschen() {
  const navigate = useNavigate();

  React.useEffect(() => { document.title = 'Konto löschen & Datenschutz | 366Transfer Portal'; }, []);

  const emailSubject = encodeURIComponent('Konto löschen');
  const emailBody = encodeURIComponent(
    'Hallo 366Transfer-Team,\n\n' +
    'ich möchte die Löschung meines Kontos und meiner persönlichen Daten beantragen.\n\n' +
    'Meine E-Mail-Adresse (im Konto registriert): \n' +
    'Mein Name (wie im Konto hinterlegt): \n' +
    'Meine Rolle: [Kunde / Fahrer]\n\n' +
    'Mit freundlichen Grüßen'
  );
  const mailtoUrl = `mailto:datenschutz@366transfer.com?subject=${emailSubject}&body=${emailBody}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="konto-loeschen-page">
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="konto-loeschen-back-btn">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <a href="/" className="flex items-baseline gap-0.5">
            <span className="text-2xl font-extrabold text-orange-500">366</span>
            <span className="text-base font-semibold text-slate-800">Transfer</span>
          </a>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white py-12 px-6 text-center">
        <h1 className="text-3xl font-bold tracking-wide">Konto und Daten löschen</h1>
        <p className="text-amber-400 text-sm mt-2 uppercase tracking-widest">366 Transfer · Mobile App</p>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-9 -mt-8 mb-12 bg-white rounded-xl shadow-lg border border-slate-100 py-10 px-6 sm:py-12 sm:px-10">
        <p className="text-base text-slate-600 leading-relaxed">
          Sie können jederzeit die Löschung Ihres 366Transfer-Kontos und der zugehörigen personenbezogenen
          Daten beantragen. Diese Seite erklärt Ihnen Schritt für Schritt, wie das funktioniert, welche
          Daten gelöscht werden und welche Daten wir ggf. aus rechtlichen Gründen aufbewahren müssen.
        </p>

        {/* Right to delete */}
        <div className="bg-blue-50 border-l-4 border-blue-600 rounded-r-md px-5 py-4 mt-6">
          <div className="flex items-center gap-2 mb-1.5">
            <Lock size={18} className="text-blue-700" />
            <strong className="text-slate-800">Ihr Recht auf Löschung</strong>
          </div>
          <p className="text-sm text-slate-700">
            Gemäß Art. 17 DSGVO („Recht auf Vergessenwerden") haben Sie das Recht, die Löschung Ihrer
            personenbezogenen Daten zu verlangen. Wir kommen Ihrem Antrag innerhalb von{' '}
            <strong>30 Tagen</strong> nach.
          </p>
        </div>

        {/* How to delete */}
        <Section icon={<Trash2 size={20} className="text-slate-700" />} title="So löschen Sie Ihr Konto">
          <p>Sie haben zwei einfache Möglichkeiten, die Kontolöschung zu beantragen:</p>

          <h3 className="text-base font-semibold text-slate-800 mt-5 flex items-center gap-2">
            <Mail size={16} className="text-orange-500" /> Option 1 — Per E-Mail (empfohlen)
          </h3>
          <p className="mt-2">Senden Sie eine E-Mail mit dem Betreff <strong>„Konto löschen"</strong> an:</p>

          <div className="text-center my-6">
            <a
              href={mailtoUrl}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-7 py-3.5 rounded-lg shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
              data-testid="delete-email-cta"
            >
              <Mail size={18} /> Jetzt Löschung per E-Mail anfordern
            </a>
          </div>

          <p>Bitte geben Sie in der E-Mail Folgendes an:</p>
          <ul className="list-disc pl-6 space-y-1.5 mt-2">
            <li>Ihre im Konto registrierte <strong>E-Mail-Adresse</strong></li>
            <li>Ihren <strong>Namen</strong> (wie im Konto hinterlegt)</li>
            <li>Ihre <strong>Rolle</strong>: Kunde oder Fahrer</li>
          </ul>

          <h3 className="text-base font-semibold text-slate-800 mt-6 flex items-center gap-2">
            <Smartphone size={16} className="text-orange-500" /> Option 2 — Direkt in der App
          </h3>
          <ol className="mt-3 space-y-3 counter-reset-step list-none pl-0">
            {[
              'Öffnen Sie die 366Transfer App auf Ihrem Gerät',
              'Melden Sie sich mit Ihren Zugangsdaten an',
              'Tippen Sie auf „Profil" in der unteren Navigationsleiste',
              'Scrollen Sie nach unten und tippen Sie auf „Konto löschen"',
              'Bestätigen Sie Ihre Entscheidung — Ihr Löschungsantrag wird sofort an unser Team übermittelt',
            ].map((step, i) => (
              <li key={i} className="flex gap-3 bg-slate-50 rounded-lg px-4 py-3 text-sm text-slate-700">
                <span className="shrink-0 w-7 h-7 rounded-full bg-slate-800 text-white font-bold text-sm flex items-center justify-center">{i + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>

          <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-md px-5 py-4 mt-5">
            <div className="flex items-center gap-2 mb-1.5">
              <AlertTriangle size={18} className="text-amber-600" />
              <strong className="text-slate-800">Hinweis</strong>
            </div>
            <p className="text-sm text-slate-700">
              Vor der endgültigen Löschung verifizieren wir Ihre Identität per E-Mail an die im Konto
              hinterlegte Adresse, um Missbrauch zu verhindern.
            </p>
          </div>
        </Section>

        {/* What gets deleted */}
        <Section icon={<Trash2 size={20} className="text-red-600" />} title="Welche Daten werden gelöscht?">
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50 text-slate-700">
                  <th className="text-left p-2.5 border-b border-slate-200">Datentyp</th>
                  <th className="text-left p-2.5 border-b border-slate-200">Status nach Löschantrag</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Account-Informationen (Name, E-Mail, Passwort)', 'Gelöscht innerhalb von 30 Tagen'],
                  ['Telefonnummer und Adresse', 'Gelöscht innerhalb von 30 Tagen'],
                  ['Profilbild', 'Gelöscht innerhalb von 30 Tagen'],
                  ['Fahrer-Dokumente (Führerschein, Ausweis, Versicherungsnachweise)', 'Gelöscht innerhalb von 90 Tagen'],
                  ['Gespeicherte Zahlungsmethoden-Verknüpfungen', 'Sofort gelöscht'],
                  ['Device-ID und IP-Logs', 'Gelöscht innerhalb von 30 Tagen'],
                  ['App-Einstellungen und Präferenzen', 'Sofort gelöscht'],
                ].map(([type, status]) => (
                  <tr key={type} className="border-b border-slate-100">
                    <td className="p-2.5 text-slate-700">{type}</td>
                    <td className="p-2.5 text-red-600 font-semibold">{status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* What must be kept */}
        <Section icon={<FolderArchive size={20} className="text-slate-700" />} title="Welche Daten müssen wir aufbewahren?">
          <div className="bg-red-50 border-l-4 border-red-500 rounded-r-md px-5 py-4 my-4">
            <strong className="text-slate-800">Gesetzliche Aufbewahrungspflichten</strong>
            <p className="text-sm text-slate-700 mt-1">
              Aus steuer- und handelsrechtlichen Gründen sind wir verpflichtet, einige Daten auch nach
              Ihrer Kontolöschung aufzubewahren. Diese werden jedoch gesperrt, nicht mehr aktiv verarbeitet
              und nach Ablauf der gesetzlichen Fristen automatisch gelöscht.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50 text-slate-700">
                  <th className="text-left p-2.5 border-b border-slate-200">Datentyp</th>
                  <th className="text-left p-2.5 border-b border-slate-200">Dauer</th>
                  <th className="text-left p-2.5 border-b border-slate-200">Rechtsgrundlage</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Rechnungen und Buchungsnachweise', '10 Jahre', '§ 147 AO (Abgabenordnung)'],
                  ['Zahlungsbelege (über Stripe/PayPal)', '10 Jahre', '§ 257 HGB (Handelsgesetzbuch)'],
                  ['Steuerrelevante Korrespondenz', '6 Jahre', '§ 147 Abs. 3 AO'],
                ].map(([type, duration, law]) => (
                  <tr key={type} className="border-b border-slate-100">
                    <td className="p-2.5 font-semibold text-slate-800">{type}</td>
                    <td className="p-2.5 text-slate-700">{duration}</td>
                    <td className="p-2.5 text-slate-600 text-xs">{law}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Duration */}
        <Section icon={<Clock size={20} className="text-slate-700" />} title="Wie lange dauert die Löschung?">
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Bestätigung Ihres Antrags:</strong> innerhalb von 48 Stunden</li>
            <li><strong>Identitätsprüfung:</strong> 1–3 Werktage</li>
            <li><strong>Tatsächliche Löschung:</strong> innerhalb von 30 Tagen (Fahrer-Dokumente: 90 Tage)</li>
            <li><strong>Schriftliche Bestätigung der abgeschlossenen Löschung:</strong> per E-Mail an die gelöschte Adresse</li>
          </ul>
        </Section>

        {/* What happens */}
        <Section icon={<HelpCircle size={20} className="text-slate-700" />} title="Was passiert, wenn ich mein Konto lösche?">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Sie können sich nicht mehr in der App anmelden</li>
            <li>Alle Ihre aktiven Buchungen werden storniert (falls noch nicht abgeschlossen)</li>
            <li>Als Fahrer verlieren Sie den Zugang zu laufenden Aufträgen — bitte schließen Sie diese vorher ab</li>
            <li>Die Löschung kann nicht rückgängig gemacht werden</li>
            <li>Sie können sich später jederzeit mit einer neuen E-Mail wieder registrieren</li>
          </ul>
        </Section>

        {/* Privacy is important */}
        <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-r-md px-5 py-4 mt-6">
          <div className="flex items-center gap-2 mb-1.5">
            <CheckCircle2 size={18} className="text-emerald-600" />
            <strong className="text-slate-800">Ihre Privatsphäre ist uns wichtig</strong>
          </div>
          <p className="text-sm text-slate-700">
            Wir respektieren Ihr Recht auf Datenschutz und bearbeiten jeden Löschungsantrag sorgfältig
            und zeitnah. Bei Fragen oder Problemen kontaktieren Sie uns jederzeit.
          </p>
        </div>

        {/* Contact */}
        <Section icon={<Phone size={20} className="text-slate-700" />} title="Kontakt">
          <p>
            <strong className="text-slate-800">366Transfer OÜ</strong><br />
            E-Mail (Datenschutz):{' '}
            <a href="mailto:datenschutz@366transfer.com" className="text-orange-500 hover:underline">datenschutz@366transfer.com</a><br />
            Web: <a href="https://366transfer.com" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">https://366transfer.com</a>
          </p>
        </Section>

        <div className="mt-10 pt-5 border-t border-slate-200 text-center text-xs text-slate-400">
          © 2026 366Transfer OÜ. Alle Rechte vorbehalten. ·{' '}
          <a href="/datenschutz" className="text-slate-500 hover:text-orange-500 hover:underline">Datenschutzerklärung</a> ·{' '}
          <a href="/agb" className="text-slate-500 hover:text-orange-500 hover:underline">AGB</a>
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-xl font-bold text-slate-800 border-l-4 border-amber-400 pl-3 mb-3 flex items-center gap-2">
        {icon}
        <span>{title}</span>
      </h2>
      <div className="text-slate-700 leading-relaxed text-sm sm:text-[15px] space-y-2">
        {children}
      </div>
    </section>
  );
}
